import PortalIdParser from 'PortalIdParser';
import omit from 'transmute/omit';

import { View as CustomerDataView } from 'customer-data-views-management/viewsSearch/types/ViewTypes';

import {
  CrmSearchFilter,
  FilterOperator,
  FiltersState,
  HiddenFilter,
  UIMode,
  View,
  ViewUpdate,
  ViewUpdateState,
} from 'framework-listing-lib/types';
import { CardProperty } from 'marketing-platform-lib/types';
import { DESC } from 'framework-listing-lib/constants/sorts';
import { VIEW_TYPE } from 'framework-listing-lib/internal/ViewTabs/constants/views';
import { combineFilters } from 'framework-listing-lib/utils/filters';
import { useFiltersForNewViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { UI_MODE } from '../../../constants';

/**
 * Creates a view request used to save views (create or update) in the BE.
 * If taking and existing view (@view is defined), it will use its current information to update a view.
 * Otherwise, it is going to return a view to create.
 */
export function buildViewUpdate({
  cardProperties,
  columns,
  currentUIMode,
  filterGroups,
  hiddenFilter,
  numberOfFrozenColumns,
  objectType,
  quickFilters,
  quickFilterPropertyNames,
  sort,
  view,
  viewUpdateState,
}: {
  cardProperties: CardProperty[];
  columns: string[];
  currentUIMode: UIMode;
  filterGroups: CrmSearchFilter[];
  hiddenFilter?: HiddenFilter;
  numberOfFrozenColumns?: number;
  objectType: string;
  quickFilters: FilterOperator[];
  quickFilterPropertyNames: string[];
  sort: FiltersState['sort'];
  view?: View;
  viewUpdateState: ViewUpdateState;
}): ViewUpdate {
  const viewFilterGroups: View['filterGroups'] = hiddenFilter
    ? combineFilters(filterGroups, hiddenFilter.filterGroups)
    : filterGroups;

  return {
    columns: columns.map((name, index) => ({
      name,
      order: index + 1,
    })),
    id: view ? String(view.id) : null,
    filterGroups: viewFilterGroups,
    filters: [],
    name: view ? view.name : '',
    ownerId: view ? view.ownerId : null,
    objectTypeId: objectType,
    portalId: PortalIdParser.get() as number,
    private: view ? view.private : false,
    quickFilters,
    quickFilterProperties: quickFilterPropertyNames.map((name) => ({ name })),
    state: {
      // The selected sort below replaces legacy sort aliases as well as sortKey.
      ...omit(['sortColumnName'], view?.state || {}),
      cardProperties,
      order: sort.order === DESC ? 1 : -1,
      sortKey: sort.property,
      mLangGroup: {
        showAll: viewUpdateState.mLangGroup.showAll,
      },
      numberOfFrozenColumns,
      uiMode:
        currentUIMode === UI_MODE.FOLDERS
          ? UI_MODE.LIST
          : String(currentUIMode),
    },
    teamId: view ? view.teamId : null,
    type: view ? view.type : VIEW_TYPE.STANDARD,
  };
}

export function getViewWithNewFilters({
  filtersForNewViews,
  viewUpdate,
}: {
  filtersForNewViews: ReturnType<typeof useFiltersForNewViews>;
  viewUpdate: ViewUpdate;
}): ViewUpdate {
  return {
    ...viewUpdate,
    filterGroups: filtersForNewViews.filterGroups,
    filters: [],
    quickFilters: filtersForNewViews.quickFilters,
  };
}

export function convertCustomerDataViewToView(
  customerDataView: CustomerDataView
): View {
  return {
    columns: customerDataView.columns.map(({ name }, index) => ({
      name,
      order: index,
    })),
    filters: [],
    // Different TS types between FLL and customer-data-views-management
    filterGroups: customerDataView.filterGroups as any,
    // Different TS types between FLL and customer-data-views-management
    quickFilters: customerDataView.quickFilters as any,
    quickFilterProperties: customerDataView.quickFilterProperties,
    /**
     * NOTE: customer-data-views-management uses the "idLabel" as the view "id"
     */
    id: customerDataView.id,
    idLabel: customerDataView.idLabel,
    name: customerDataView.name,
    namespace: customerDataView.namespaceName,
    objectTypeId: customerDataView.objectTypeId,
    ownerId: customerDataView.ownerId || null,
    portalId: PortalIdParser.get() as number,
    private: customerDataView.private,
    // Different TS types between FLL and customer-data-views-management
    state: customerDataView.state as any,
    teamId: customerDataView.teamId || null,
    type: customerDataView.type as any,
  };
}

export function convertViewToCustomerDataView({
  view,
}: {
  view: View;
}): CustomerDataView {
  return {
    id: view.id,
    name: view.name,
    objectTypeId: view.objectTypeId,
    idLabel: view.idLabel,
    ownerId: view.ownerId || undefined,
    teamId: view.teamId || undefined,
    private: view.private,
    type: view.type,
    columns: view.columns,
    // Different TS types between FLL and customer-data-views-management
    state: view.state as any,
    namespaceName: view.namespace,
    filterGroups: view.filterGroups,
    quickFilters: view.quickFilters,
    quickFilterProperties: view.quickFilterProperties,
  };
}

export function convertViewUpdateToCustomerDataView({
  viewUpdate,
  namespace,
}: {
  viewUpdate: ViewUpdate;
  namespace: string;
}): CustomerDataView {
  return convertViewToCustomerDataView({
    view: {
      ...viewUpdate,
      id: viewUpdate.id || '',
      namespace,
    },
  });
}
